import { useMemo, useState } from "react";
import {
  Alert, Box, Chip, CircularProgress, MenuItem, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { accountsApi, type AccountDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

/**
 * acc.coa.list — read-only Chart of Accounts browser.
 *
 * Source of truth is the seeded CoA template (SeedData.DefaultCoaTemplate) — accounts can
 * be created today only through that seeder, so the production UI is intentionally read-only
 * until an "Add account" write API is exposed. The S5 chart-of-accounts spec keeps codes
 * stable per CLAUDE.md (1300/1410/2100/4100/4200/4300/5100/2200) so handlers can assert on
 * them; introducing free-form UI mutation here would risk drifting those.
 */
const ACCOUNT_TYPES = ["Asset", "Liability", "Equity", "Revenue", "Expense"] as const;

const TYPE_COLOR: Record<string, "primary" | "warning" | "success" | "info" | "default"> = {
  Asset:     "primary",
  Liability: "warning",
  Equity:    "default",
  Revenue:   "success",
  Expense:   "info",
};

export function ChartOfAccountsPage() {
  const [search, setSearch] = useState("");
  const [type, setType] = useState<string>("");
  const [isActive, setIsActive] = useState<string>("active");

  const query = useQuery({
    queryKey: ["chart-of-accounts", { search, type, isActive }],
    queryFn: () => accountsApi.list({
      search: search.trim() || undefined,
      type: type || undefined,
      isActive: isActive === "all" ? undefined : isActive === "active",
      pageSize: 500,
    }),
  });

  const grouped = useMemo(() => {
    const rows = query.data?.items ?? [];
    const map = new Map<string, AccountDto[]>();
    for (const a of rows) {
      const key = a.accountType;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    }
    return Array.from(map.entries()).sort((a, b) =>
      ACCOUNT_TYPES.indexOf(a[0] as typeof ACCOUNT_TYPES[number]) -
      ACCOUNT_TYPES.indexOf(b[0] as typeof ACCOUNT_TYPES[number]));
  }, [query.data]);

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        Chart of accounts
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Read-only browser of the seeded chart of accounts. The pharmacy GL handlers
        (sales invoice post, purchase invoice post, manual journal) reference these by
        <b> Code</b> — keep that contract in mind before requesting writable CoA edits.
      </Alert>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="Search (code or name)" size="small" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 240 }} />
          <TextField
            select label="Type" size="small" value={type}
            onChange={(e) => setType(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">All</MenuItem>
            {ACCOUNT_TYPES.map(t => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>
          <TextField
            select label="Status" size="small" value={isActive}
            onChange={(e) => setIsActive(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="active">Active only</MenuItem>
            <MenuItem value="inactive">Inactive only</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {query.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>
      )}
      {query.isLoading && (
        <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}

      {grouped.map(([accountType, rows]) => (
        <Paper key={accountType} sx={{ mb: 2 }}>
          <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              size="small" color={TYPE_COLOR[accountType] ?? "default"}
              label={accountType} />
            <Typography variant="subtitle1" fontWeight={600}>
              {rows.length} account{rows.length === 1 ? "" : "s"}
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={120}>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell align="center" width={120}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(a => (
                  <TableRow key={a.id} hover>
                    <TableCell><b>{a.code}</b></TableCell>
                    <TableCell>{a.name}</TableCell>
                    <TableCell>{a.accountGroupName ?? "—"}</TableCell>
                    <TableCell align="center">
                      <Chip size="small"
                        color={a.isActive ? "success" : "default"}
                        label={a.isActive ? "Active" : "Inactive"} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}

      {!query.isLoading && grouped.length === 0 && (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No accounts match the current filter.</Typography>
        </Paper>
      )}
    </Box>
  );
}
